import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, runInAction, set } from "mobx";
import moment from "moment";
import { routeLocationAffiliateCouponPost, routeLocationAffiliateCouponsGet, routeLocationAffiliateInviteGet, routeLocationAffiliateLinkPost, routeLocationAffiliatePatch, routeLocationAffiliatePayoutsGet, routeLocationAffiliateTransactionsGet } from "@devowl-wp/api-real-commerce";
import { AffiliateCoupon } from "./affiliateCoupon.js";
import { RealCommerceError } from "../../../exceptions/realCommerceError.js";
import { realCommerceRequest } from "../../../utils/realCommerceRequest.js";
class Affiliate {
    get affiliate() {
        return this.data.affiliate;
    }
    get commissions() {
        return this.data.commissions;
    }
    get invoiceAddress() {
        return this.affiliate.invoiceAddress;
    }
    get paymentDetails() {
        return this.affiliate.paymentDetails;
    }
    get isManuallyReviewed() {
        return this.affiliate.isManuallyReviewed;
    }
    get commissionPlanned() {
        return this.affiliate.commissionPlanned;
    }
    get commissionPlannedChangeDate() {
        return this.affiliate.commissionPlannedChangeDate ? new Date(this.affiliate.commissionPlannedChangeDate) : undefined;
    }
    get nextPayoutDate() {
        const now = new Date();
        // 15th of next calendar month. "Next" is relative to the calendar month, not to whether today
        // is before/after the 15th: payouts run monthly on the 15th, so an affiliate who hits the
        // threshold on the 16th waits a full month, not a few days.
        return new Date(now.getFullYear(), now.getMonth() + 1, 15);
    }
    get amountUntilThreshold() {
        return Math.max(0, this.affiliate.payoutThreshold - this.commissions.totalUnpaid);
    }
    get isOnTrackForNextPayout() {
        return this.commissions.totalUnpaid >= this.affiliate.payoutThreshold;
    }
    get firstPayoutMonth() {
        const dates = Array.from(this.payouts.values()).map(({ createdAt })=>new Date(createdAt).getTime());
        return dates.length ? new Date(Math.min(...dates)) : undefined;
    }
    constructor(data, store){
        this.isBusyCoupons = false;
        this.isBusyTransactions = false;
        this.isBusyPayouts = false;
        this.coupons = new Map();
        this.statistics = [];
        this.transactions = new Map();
        this.payouts = new Map();
        this.transactionFilters = observable.object({
            dates: [
                undefined,
                undefined
            ]
        }, {}, {
            deep: false
        });
        this.createLink = flow(function*(target) {
            yield this.store.probablyRenewJwt();
            try {
                const { link } = yield realCommerceRequest({
                    location: routeLocationAffiliateLinkPost,
                    request: {
                        link: {
                            target
                        }
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                return link;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.fetchTransactions = flow(function*() {
            this.isBusyTransactions = true;
            yield this.store.probablyRenewJwt();
            try {
                const { dates: [from, to] } = this.transactionFilters;
                const { statistics, transactions } = yield realCommerceRequest({
                    location: routeLocationAffiliateTransactionsGet,
                    params: {
                        from: from.toDate(),
                        to: to.toDate()
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.statistics = statistics;
                for (const row of transactions){
                    this.transactions.set(row.id, row);
                }
                // Remove stale transactions
                const activeIds = transactions.map(({ id })=>id);
                Array.from(this.transactions.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.transactions.delete(potentialStaleId);
                    }
                });
                // Additionally, update the global header
                this.store.fetchAffiliate();
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.isBusyTransactions = false;
            }
        });
        this.fetchPayouts = flow(function*() {
            this.isBusyPayouts = true;
            yield this.store.probablyRenewJwt();
            try {
                const { payouts } = yield realCommerceRequest({
                    location: routeLocationAffiliatePayoutsGet,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                for (const row of payouts){
                    this.payouts.set(row.id, row);
                }
                // Remove stale payouts
                const activeIds = payouts.map(({ id })=>id);
                Array.from(this.payouts.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.payouts.delete(potentialStaleId);
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.isBusyPayouts = false;
            }
        });
        this.fetchCoupons = flow(function*() {
            this.isBusyCoupons = true;
            yield this.store.probablyRenewJwt();
            try {
                const { coupons } = yield realCommerceRequest({
                    location: routeLocationAffiliateCouponsGet,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                for (const row of coupons){
                    const { id } = row;
                    const existing = this.coupons.get(id);
                    if (existing) {
                        set(existing.data, row);
                    } else {
                        this.coupons.set(id, new AffiliateCoupon(row, this));
                    }
                }
                // Remove stale coupons
                const activeIds = coupons.map(({ id })=>id);
                Array.from(this.coupons.keys()).forEach((potentialStaleId)=>{
                    if (activeIds.indexOf(potentialStaleId) === -1) {
                        this.coupons.delete(potentialStaleId);
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.isBusyCoupons = false;
            }
        });
        this.createCoupon = flow(function*(data) {
            yield this.store.probablyRenewJwt();
            try {
                const { coupon } = yield realCommerceRequest({
                    location: routeLocationAffiliateCouponPost,
                    request: {
                        coupon: data
                    },
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.fetchCoupons();
                return coupon;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.updateDetails = flow(function*(data) {
            yield this.store.probablyRenewJwt();
            try {
                const { affiliate } = yield realCommerceRequest({
                    location: routeLocationAffiliatePatch,
                    request: {
                        affiliate: data
                    },
                    multipart: true,
                    settings: {
                        headers: this.store.authorizationHeader
                    }
                });
                this.data.affiliate = affiliate;
                return affiliate;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.store = store;
        this.applyTransactionDates([
            moment().subtract(30, "days"),
            moment()
        ]);
        runInAction(()=>{
            this.data = data;
        });
    }
    /**
     * This action does not actually refetch the transaction, you need to act on your components!
     *
     * @param dates
     */ applyTransactionDates(dates) {
        this.transactionFilters.dates = dates;
    }
    static{
        this.loadInvite = flow(function*(uuid) {
            try {
                const { invite } = yield realCommerceRequest({
                    location: routeLocationAffiliateInviteGet,
                    params: {
                        uuid
                    }
                });
                return invite;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof IRouteResponseAffiliateGet === "undefined" ? Object : IRouteResponseAffiliateGet)
], Affiliate.prototype, "data", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "isBusyCoupons", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "isBusyTransactions", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "isBusyPayouts", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "coupons", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], Affiliate.prototype, "statistics", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "transactions", void 0);
_ts_decorate([
    observable
], Affiliate.prototype, "payouts", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "affiliate", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "commissions", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "invoiceAddress", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "paymentDetails", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "isManuallyReviewed", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "commissionPlanned", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "commissionPlannedChangeDate", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", typeof Date === "undefined" ? Object : Date),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "nextPayoutDate", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Number),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "amountUntilThreshold", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Boolean),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "isOnTrackForNextPayout", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", Object),
    _ts_metadata("design:paramtypes", [])
], Affiliate.prototype, "firstPayoutMonth", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        Object
    ]),
    _ts_metadata("design:returntype", void 0)
], Affiliate.prototype, "applyTransactionDates", null);
export { Affiliate };
